import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { prehashPassword } from '@faceup/crypto'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import useReportAuth from '../utils/useReportAuth'
import { clearUserData, getUserData, persistUserDataSlice } from '../utils/userDataStorage'
import { useAccessKey } from './AccessKeyProvider'
import { useClient } from './ClientProvider'

const LOGOUT_TIMEOUT = 30 * 1000
const PIN_SALT = 'cZuUtYfvMpUSgjLKSzDvgw'

type Settings = { biometrics?: boolean | null } | null

type AppLockContextProps = {
  isAuthenticated: boolean
  login: {
    hasAccount: boolean
    authenticate: (pin: string) => Promise<boolean>
    biometricsAuthentication: () => void
    signUp: (pin: string) => void
  }
  logout: () => Promise<void>
  settings: Settings
  updateSettings: {
    setBiometrics: (enabled: boolean) => void
  }
  isSetUp: () => boolean
}

const AppLockContext = createContext<AppLockContextProps>({
  isAuthenticated: false,
  login: {
    hasAccount: false,
    authenticate: async () => false,
    biometricsAuthentication: () => undefined,
    signUp: () => undefined,
  },
  logout: () => Promise.resolve(undefined),
  settings: {},
  updateSettings: {
    setBiometrics: () => undefined,
  },
  isSetUp: () => true,
})

type AppLockProviderProps = {
  children: ReactNode
}

/**
 * App-only PIN/biometrics lock. On the web it is a no-op: isAuthenticated is
 * always true. In the app it gates the UI behind a PIN, auto-locks after 30s in
 * the background, and persists the authPin/settings slice of USER_DATA.
 */
export const AppLockProvider = ({ children }: AppLockProviderProps) => {
  const { client } = useClient()
  const { clearAccessKey } = useAccessKey()
  const { logout: reportLogout } = useReportAuth()
  const isClientApp = client === 'app'

  const data = isClientApp ? getUserData() : null

  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authPin, setAuthPin] = useState<string | null>(data?.authPin ?? null)
  const [settings, setSettings] = useState<Settings>(data?.settings ?? null)

  useEffect(() => {
    if (!isClientApp) {
      return
    }
    // Auto-lock when the app goes to the background for too long.
    let logoutTimeout: number
    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        logoutTimeout = window.setTimeout(() => setIsAuthenticated(false), LOGOUT_TIMEOUT)
      } else {
        clearTimeout(logoutTimeout)
      }
    })
    return () => {
      listener.then(handle => handle.remove())
      clearTimeout(logoutTimeout)
    }
  }, [isClientApp])

  useEffect(() => {
    if (!isClientApp) {
      return
    }
    persistUserDataSlice({ authPin, settings })
  }, [authPin, settings, isClientApp])

  const prehashPin = async (pin: string) => {
    const prehashedPassword = await prehashPassword({ password: pin, salt: PIN_SALT })
    if (prehashedPassword.isErr()) {
      throw new Error(prehashedPassword.error.message)
    }
    return prehashedPassword.value.passwordKeyPrehash
  }

  const authenticate = async (pin: string) => {
    const prehashedPin = await prehashPin(pin)
    const isPinValid = prehashedPin === authPin
    setIsAuthenticated(isPinValid)
    return isPinValid
  }

  const signUp = async (pin: string) => {
    setAuthPin(await prehashPin(pin))
    setIsAuthenticated(true)
  }

  const logout = async () => {
    await reportLogout()
    setIsAuthenticated(false)
    setSettings({})
    setAuthPin(null)
    clearUserData()
    // The institution lives in AccessKeyProvider now; clear it too, or
    // HomeRedirect re-enters the old source after a forgotten-PIN reset.
    clearAccessKey()
  }

  const isSetUp = () => {
    if (!isClientApp) {
      return true
    }
    // Hide biometrics for android
    if (Capacitor.getPlatform() !== 'ios') {
      return true
    }
    return settings?.biometrics !== undefined
  }

  // Not memoized: the auth closures are recreated each render anyway and this
  // provider re-renders rarely (PIN/lock state changes only).
  const value: AppLockContextProps = {
    isAuthenticated: isAuthenticated || !isClientApp,
    login: {
      hasAccount: Boolean(authPin),
      authenticate,
      // When logging with biometrics, no PIN or other information is required
      biometricsAuthentication: () => setIsAuthenticated(true),
      signUp,
    },
    logout,
    settings,
    updateSettings: {
      setBiometrics: (enabled: boolean) =>
        setSettings(current => ({ ...(current ?? {}), biometrics: enabled })),
    },
    isSetUp,
  }

  return <AppLockContext.Provider value={value}>{children}</AppLockContext.Provider>
}

export const useAppLock = () => useContext(AppLockContext)
